import { useEffect, useState } from "react";
import api, { fmtDate, formatApiError, hrs } from "@/lib/apiClient";
import AdminLayout from "@/layouts/AdminLayout";
import { adminNav } from "@/lib/navConfig";
import { PageHeader } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Lock, LockOpen, ChevronDown, Download, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";

const eur = (n) => "€" + Number(n || 0).toLocaleString("en-IE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function PayrollPeriods() {
  const [periods, setPeriods] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", start: "", end: "" });
  const [confirmLock, setConfirmLock] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [summaries, setSummaries] = useState({});
  const [busy, setBusy] = useState("");

  const load = () => api.get("/payroll-periods").then((r) => setPeriods(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name || !form.start || !form.end) { toast.error("Name and date range required"); return; }
    try { await api.post("/payroll-periods", form); toast.success("Payroll period created"); setOpen(false); setForm({ name: "", start: "", end: "" }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const doLock = async () => {
    try { await api.post(`/payroll-periods/${confirmLock.id}/lock`); toast.success("Period locked; approved timesheets in range are now locked"); setConfirmLock(null); load(); setSummaries({}); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggle = async (p) => {
    if (expanded === p.id) { setExpanded(null); return; }
    setExpanded(p.id);
    if (!summaries[p.id]) {
      try { const r = await api.get(`/payroll-periods/${p.id}/summary`); setSummaries((s) => ({ ...s, [p.id]: r.data })); }
      catch { toast.error("Failed to load summary"); }
    }
  };

  const downloadPayroll = async (p, format) => {
    setBusy(`${p.id}-${format}`);
    try {
      const res = await api.get("/reports/payroll", { params: { format, start: p.start, end: p.end }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `payroll_${(p.name || "period").replace(/\s+/g, "_")}.${format === "xlsx" ? "xlsx" : "csv"}`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Payroll file downloaded");
    } catch { toast.error("Export failed"); } finally { setBusy(""); }
  };

  return (
    <AdminLayout nav={adminNav} title="Payroll Periods">
      <PageHeader title="Payroll Periods" subtitle="Lock approved timesheets and view fully-loaded employment costs per period">
        <Button data-testid="new-period-btn" onClick={() => setOpen(true)} className="bg-slate-900 text-white">+ New period</Button>
      </PageHeader>

      <div className="space-y-3">
        {periods.length === 0 && <div className="text-zinc-400 text-sm">No payroll periods yet.</div>}
        {periods.map((p) => {
          const sum = summaries[p.id];
          return (
            <div key={p.id} data-testid={`period-${p.id}`} className="bg-white border border-zinc-200 rounded-md">
              <div className="flex items-center justify-between p-4">
                <button data-testid={`period-toggle-${p.id}`} onClick={() => toggle(p)} className="flex items-center gap-3 text-left">
                  <ChevronDown className={`h-4 w-4 text-zinc-400 transition-transform ${expanded === p.id ? "rotate-180" : ""}`} />
                  <div>
                    <div className="font-semibold text-sm">{p.name}</div>
                    <div className="text-xs text-zinc-500 num">{fmtDate(p.start)} – {fmtDate(p.end)}</div>
                  </div>
                </button>
                <div className="flex items-center gap-2">
                  <Button data-testid={`pp-payroll-csv-${p.id}`} size="sm" variant="outline" disabled={busy === `${p.id}-csv`} onClick={() => downloadPayroll(p, "csv")}><Download className="h-3.5 w-3.5 mr-1" />CSV</Button>
                  <Button data-testid={`pp-payroll-xlsx-${p.id}`} size="sm" className="bg-emerald-600 text-white" disabled={busy === `${p.id}-xlsx`} onClick={() => downloadPayroll(p, "xlsx")}><FileSpreadsheet className="h-3.5 w-3.5 mr-1" />Payroll file</Button>
                  {p.locked ? (
                    <span data-testid={`period-locked-${p.id}`} className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded"><Lock className="h-3.5 w-3.5" />Locked</span>
                  ) : (
                    <>
                      <span className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-amber-700 bg-amber-50 px-2.5 py-1 rounded"><LockOpen className="h-3.5 w-3.5" />Open</span>
                      <Button data-testid={`period-lock-btn-${p.id}`} size="sm" className="bg-slate-900 text-white" onClick={() => setConfirmLock(p)}>Lock period</Button>
                    </>
                  )}
                </div>
              </div>
              {expanded === p.id && (
                <div data-testid={`period-summary-${p.id}`} className="border-t border-zinc-100 p-4 bg-zinc-50/50">
                  {!sum ? <div className="text-zinc-400 text-sm">Loading…</div> : (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <Stat label="Timesheets" value={sum.timesheet_count} />
                      <Stat label="Employees" value={sum.employee_count} />
                      <Stat label="Paid Hours" value={hrs(sum.total_hours)} />
                      <Stat label="Basic Wages" value={eur(sum.total_wages)} />
                      <Stat label="Employer PRSI" value={eur(sum.employer_prsi)} />
                      <Stat label="Holiday Accrual" value={eur(sum.holiday_accrual)} />
                      <Stat label="Fully-Loaded Cost" value={eur(sum.fully_loaded_cost)} highlight />
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>New payroll period</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input data-testid="pp-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Week 12 Jun 2026" className="mt-1" /></div>
            <div><Label>Start date</Label><Input data-testid="pp-start" type="date" value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} className="mt-1" /></div>
            <div><Label>End date</Label><Input data-testid="pp-end" type="date" value={form.end} onChange={(e) => setForm({ ...form, end: e.target.value })} className="mt-1" /></div>
            <Button data-testid="pp-submit" onClick={create} className="w-full bg-slate-900 text-white">Create period</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirmLock} onOpenChange={(v) => !v && setConfirmLock(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Lock payroll period?</DialogTitle></DialogHeader>
          <p className="text-sm text-zinc-600">This locks all <strong>approved</strong> timesheets between {confirmLock && fmtDate(confirmLock.start)} and {confirmLock && fmtDate(confirmLock.end)}. Locked timesheets can no longer be edited. This cannot be undone.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmLock(null)}>Cancel</Button>
            <Button data-testid="confirm-lock-btn" className="bg-slate-900 text-white" onClick={doLock}>Lock period</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}

function Stat({ label, value, highlight }) {
  return (
    <div className={`rounded-md p-3 border ${highlight ? "bg-amber-50 border-amber-200" : "bg-white border-zinc-200"}`}>
      <div className="text-xs uppercase tracking-wider text-zinc-500">{label}</div>
      <div className={`num font-bold mt-1 ${highlight ? "text-amber-700 text-lg" : "text-slate-900"}`}>{value}</div>
    </div>
  );
}
